import { converterDataHoraBr } from "_utils/Data";
import { PrimaryButton } from "components/Button/style";
import { StatusBadge } from "components/StatusBadge";
import { useAbrirProcesso } from "features/core/pages/Financeiro/Pages/PagamentoTaxa/CriarLote/hooks/useAbrirProcesso";
import { useState } from "react";
import { FaPlus } from "react-icons/fa";
import { BotaoMoverTipo } from "../../components/BotaoMoverTipo";
import { ModalRetorno, useModalRetorno } from "../../components/ModalRetorno";
import {
  IColunaTabelaPadrao,
  TabelaPadrao,
} from "../../components/TabelaPadrao";
import { ORDENACAO_SOLICITACAO_PADRAO } from "../../constants/filtrosSolicitacao";
import {
  chaveStatusAutorizacao,
  labelStatus,
  varianteStatus,
} from "../../constants/status";
import { TIPO_SOLICITACAO_PLACA } from "../../constants/tipoSolicitacaoPlaca";
import { DirecaoOrdenacao } from "../../interfaces/IOrdenacao";
import { IResultadoImportacaoPlanilha } from "../../interfaces/IResultadoImportacaoPlanilha";
import {
  IDadosSolicitarAutorizacao,
  IResultadoCriacaoLote,
} from "../../interfaces/ILoteCriado";
import { ISolicitacaoPlaca } from "../../interfaces/ISolicitacaoPlaca";
import { SolicitarAutorizacaoModal } from "../../modals/SolicitarAutorizacaoModal";
import { SolicitarManualmenteModal } from "../../modals/SolicitarManualmenteModal";
import { LinkProcesso } from "./style";

const SEM_VALOR = "—";
const PESO_PDI_EMPRESA = 4;

// flex-basis da tabela é 0: grow proporcional ao mínimo mantém a coluna no mínimo em tela estreita
function larguraFlexivel(minimoPx: number) {
  return { minWidth: `${minimoPx}px`, grow: minimoPx / 100 };
}

interface IProps {
  solicitacoes: ISolicitacaoPlaca[];
  totalRegistros: number;
  carregando: boolean;
  limite: number;
  reiniciarPaginacao: boolean;
  onMudarPagina: (pagina: number) => void;
  onMudarLimite: (limite: number) => void;
  onOrdenar: (campo: string, direcao: DirecaoOrdenacao) => void;
  onExportar: () => void;
  exportando: boolean;
  importarPlanilha: (arquivo: File) => Promise<IResultadoImportacaoPlanilha>;
  enviandoPlanilha: boolean;
  onTotalAlterado: () => void;
  onMovidas: () => void;
  criarLote: (
    idsSolicitacao: number[],
    dados: IDadosSolicitarAutorizacao
  ) => Promise<IResultadoCriacaoLote | null>;
  criandoLote: boolean;
}

export function Listar({
  solicitacoes,
  totalRegistros,
  carregando,
  limite,
  reiniciarPaginacao,
  onMudarPagina,
  onMudarLimite,
  onOrdenar,
  onExportar,
  exportando,
  importarPlanilha,
  enviandoPlanilha,
  onTotalAlterado,
  onMovidas,
  criarLote,
  criandoLote,
}: IProps) {
  const [selecionados, setSelecionados] = useState<ISolicitacaoPlaca[]>([]);
  const [showModalAutorizacao, setShowModalAutorizacao] = useState(false);
  const [showModalManual, setShowModalManual] = useState(false);
  const [limparSelecao, setLimparSelecao] = useState(0);
  const { redirecionarProcesso } = useAbrirProcesso();
  const { mostrarRetorno, propsModalRetorno } = useModalRetorno();

  async function confirmarPlanilha(arquivo: File) {
    const resultado = await importarPlanilha(arquivo);
    if (resultado.sucesso) {
      mostrarRetorno(
        "Solicitar manualmente",
        "Solicitações incluídas com sucesso!"
      );
      onTotalAlterado();
    }
    return resultado;
  }

  async function confirmarAutorizacao(dados: IDadosSolicitarAutorizacao) {
    const resultado = await criarLote(
      selecionados.map(({ id }) => id),
      dados
    );
    if (!resultado) {
      return false;
    }
    mostrarRetorno(
      "Solicitar autorização",
      "Autorizações solicitadas com sucesso!"
    );
    onTotalAlterado();
    return true;
  }

  const colunas: IColunaTabelaPadrao<ISolicitacaoPlaca>[] = [
    {
      chave: "dataSolicitacao",
      titulo: "Solicitação",
      selector: ({ dataSolicitacao }) =>
        converterDataHoraBr(dataSolicitacao) ?? SEM_VALOR,
      sortable: true,
      sortField: "dataSolicitacao",
      width: "170px",
    },
    {
      chave: "pdi",
      titulo: "PDI",
      selector: ({ pdi }) => pdi ?? SEM_VALOR,
      sortable: true,
      sortField: "pdi",
      grow: PESO_PDI_EMPRESA,
    },
    {
      chave: "empresa",
      titulo: "Empresa",
      selector: ({ empresa }) => empresa ?? SEM_VALOR,
      sortable: true,
      sortField: "empresa",
      grow: PESO_PDI_EMPRESA,
    },
    {
      chave: "placa",
      titulo: "Placa",
      selector: ({ placa }) => placa,
      sortable: true,
      sortField: "placa",
      ...larguraFlexivel(110),
    },
    {
      chave: "processo",
      titulo: "Processo",
      selector: ({ idProcesso }) => idProcesso ?? SEM_VALOR,
      cell: ({ idProcesso }) =>
        idProcesso ? (
          <LinkProcesso
            type="button"
            onClick={() => redirecionarProcesso(idProcesso)}
          >
            {idProcesso}
          </LinkProcesso>
        ) : (
          SEM_VALOR
        ),
      sortable: true,
      sortField: "idProcesso",
      ...larguraFlexivel(120),
    },
    {
      chave: "status",
      titulo: "Status",
      selector: ({ statusAutorizacao }) => statusAutorizacao,
      cell: ({ statusAutorizacao }) => {
        const chave = chaveStatusAutorizacao(statusAutorizacao);
        return (
          <StatusBadge
            label={labelStatus(chave)}
            variant={varianteStatus(chave)}
          />
        );
      },
      sortable: true,
      sortField: "status",
      width: "120px",
    },
  ];

  return (
    <>
      <ModalRetorno {...propsModalRetorno} />
      <SolicitarAutorizacaoModal
        show={showModalAutorizacao}
        handleClose={() => setShowModalAutorizacao(false)}
        onConfirmar={confirmarAutorizacao}
        carregando={criandoLote}
      />
      <SolicitarManualmenteModal
        show={showModalManual}
        handleClose={() => setShowModalManual(false)}
        onConfirmar={confirmarPlanilha}
        carregando={enviandoPlanilha}
      />
      <TabelaPadrao
        titulo="Solicitações pendentes"
        chaveConfigColunas="solicitacaoPlaca:solicitacoes:colunas"
        limitarALargura
        colunas={colunas}
        dados={solicitacoes}
        carregando={carregando}
        totalRegistros={totalRegistros}
        limite={limite}
        colunaOrdenadaInicial={ORDENACAO_SOLICITACAO_PADRAO.campo}
        reiniciarPaginacao={reiniciarPaginacao}
        limparSelecao={limparSelecao}
        onMudarPagina={onMudarPagina}
        onMudarLimite={onMudarLimite}
        onOrdenar={onOrdenar}
        onExportar={onExportar}
        exportando={exportando}
        selecionavel
        onSelecionar={setSelecionados}
        botoesExtras={
          <>
            <PrimaryButton
              type="button"
              outline
              variante="primary"
              onClick={() => setShowModalManual(true)}
            >
              <FaPlus />
              Solicitar manualmente
            </PrimaryButton>
            <BotaoMoverTipo
              idsSolicitacao={selecionados.map(({ id }) => id)}
              destino={TIPO_SOLICITACAO_PLACA.TERCEIROS}
              onMovidas={() => {
                setLimparSelecao((valor) => valor + 1);
                onMovidas();
              }}
            />
            <PrimaryButton
              type="button"
              disabled={selecionados.length === 0}
              onClick={() => setShowModalAutorizacao(true)}
            >
              Solicitar autorização
            </PrimaryButton>
          </>
        }
      />
    </>
  );
}
